import React, { useRef, useState } from "react";
import { createChat } from "../../api/api";
import MultiUserSearch from "../messaging_page/MultiUserSearch";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * Create an organization channel with at least one other member.
 * @param {Object} props
 * @param {number} props.userId
 * @param {number} props.orgId
 * @param {Array<object>} props.candidateUsers
 * @param {() => void} props.onClose
 * @param {(chat: object) => void} props.onChatCreated
 * @param {boolean} [props.asOrgAdmin] - Org account creating without being a chat member
 * @returns {JSX.Element}
 */
function NewOrgChannelModal({
  userId,
  orgId,
  candidateUsers,
  onClose,
  onChatCreated,
  asOrgAdmin = false,
}) {
  const [name, setName] = useState("");
  const [selectionCount, setSelectionCount] = useState(0);
  const nameInputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const minDialogBodyPx = Math.min(480 + Math.min(selectionCount, 16) * 42, 900);

  /**
   * @param {Array<object>} selectedUsers
   * @returns {Promise<void>}
   */
  const handleCreateChat = async (selectedUsers) => {
    if (!name.trim()) {
      alert("Please enter a channel name.");
      return;
    }
    if (selectedUsers.length < 1) {
      alert("Add at least one other member.");
      return;
    }

    try {
      const members = asOrgAdmin
        ? selectedUsers.map((u) => ({ user_id: u.id, role: "member" }))
        : [
            { user_id: userId, role: "admin" },
            ...selectedUsers.map((u) => ({ user_id: u.id, role: "member" })),
          ];

      // Org-admin-created channels need an admin when members are present.
      if (asOrgAdmin && members.length > 0) {
        members[0].role = "admin";
      }

      const res = await createChat({
        chat_type: "group",
        name: name.trim(),
        org_id: orgId,
        space_type: "channel",
        is_default: false,
        created_by: asOrgAdmin ? 0 : userId,
        members,
      });

      if (res.success && res.data) {
        onChatCreated(res.data);
        onClose();
      } else {
        alert(res.error || "Failed to create channel.");
      }
    } catch (error) {
      console.error("Create org channel error:", error);
      alert("Failed to create channel. Please try again.");
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[min(92vh,920px)] min-w-[min(100%,35rem)] flex-col gap-0 overflow-auto p-0 sm:max-w-md resize-y"
        style={{ minHeight: `min(${minDialogBodyPx}px, 88vh)` }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameInputRef.current?.focus();
        }}
      >
        <DialogHeader className="shrink-0 space-y-0 border-0 px-6 pb-0 pt-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                New channel
              </DialogTitle>
              <DialogDescription className="mt-1.5">
                Name the channel and add organization members.
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-ui-muted-foreground"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="space-y-3 px-6 pt-4">
          <Input
            ref={nameInputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Channel name"
            aria-label="Channel name"
            className="h-10"
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 pb-6 pt-3">
          <MultiUserSearch
            userId={userId}
            onSubmit={handleCreateChat}
            submitText="Create channel"
            candidateUsers={candidateUsers}
            onSelectionSizeChange={setSelectionCount}
            autoFocus={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewOrgChannelModal;
